import { connect, useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';
import { useCallback, useRef } from 'react';
import { Form, FormRenderProps } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { useUnmount } from 'react-use';
import cn from 'classnames';

import { Row } from 'UIPackage/FormModule/components/Row';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';

import { RootActions } from '../../../CorePackage/MainModule/types/RootActions';
import { Button } from '../../../UIPackage/MainModule/components/Button';
import { TextInputField } from '../../../UIPackage/MainModule/components/TextInputField';
import { IRootState } from '../../../CorePackage/MainModule/interfaces/IRootState';
import styles from './ChangeKnoInPlanForm.module.scss';
import { makeRequired } from '../../../CorePackage/MainModule/validators/isRequired';
import { getKnmPlanFindData } from '../../../KnmPackage/MainModule/selectors/knmSelectors';
import { startProcessFormSubmit } from '../../../ChangeKnoInPlanPackage/actions/ChangeKnoInPlanActions';
import { ErvkKnoOrganisationSearch } from '../../../ChangeKnoInPlanPackage/components/ErvkKnoOrganisationSearch';
import { PlanKnoChangeHistory } from '../../../ChangeKnoInPlanPackage/components/PlanKnoChangeHistory';
import { ErknmPlanIdField } from '../../../KnmErknmPackage/ErknmInfoModule/components/ErknmPlanIdField';
import { IKnmPlanFind } from '../../../APIPackage/KnmModule/interfaces/IKnmPlanFind';
import { REQUIRED_ERROR } from '../../../CorePackage/MainModule/constants/validationErrors';
import { knmReset } from '../../../KnmPackage/MainModule/actions/knmActions';
import { IChangeKnoInPlanForm } from '../interfaces/IChangeKnoInPlanForm';

type ChangeKnoInPlanFormRenderProps = FormRenderProps<IChangeKnoInPlanForm>;

interface IChangeKnoInPlanFormProps {
  knmPlanFindData?: IKnmPlanFind | null;
}

const ChangeKnoInPlanFormWithoutStore = ({ knmPlanFindData }: IChangeKnoInPlanFormProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const formRef = useRef<ChangeKnoInPlanFormRenderProps['form']>();

  const handleFormSubmit = useCallback(
    (values: IChangeKnoInPlanForm) => {
      if (knmPlanFindData && values?.stpNumber && values?.ervkKnoOrganization) {
        dispatch(startProcessFormSubmit(knmPlanFindData, values.stpNumber, values.ervkKnoOrganization));
      }
    },
    [dispatch, knmPlanFindData],
  );

  useUnmount(() => {
    formRef.current?.restart();
    dispatch(knmReset());
  });

  function renderForm({ handleSubmit, form, values, invalid }: FormRenderProps<IChangeKnoInPlanForm>) {
    formRef.current = form;
    const { stpNumber, ervkKnoOrganization } = values;

    return (
      <form onSubmit={handleSubmit}>
        <div>
          <Row>
            <FieldBlock>
              <div style={{ display: 'flex' }}>
                <span style={{ fontWeight: 'bold', fontSize: 'xx-large' }}>Изменения КНО в плане</span>
                <Button
                  className={cn('shared-btn-add', styles.ButtonSave)}
                  type="submit"
                  color="primary"
                  size="medium"
                  text="Сохранить"
                  visuallyDisabled={invalid || !ervkKnoOrganization}
                />
              </div>
            </FieldBlock>
          </Row>
          <Row>
            <FieldBlock
              label={
                <>
                  Номер заявки в СТП ФГИС ЕРКНМ
                  {!stpNumber && <span className={styles.RedMark}> *</span>}
                </>
              }
              placementLabel="left"
            >
              <TextInputField className="shared-row-field" name="stpNumber" validate={makeRequired(REQUIRED_ERROR)} />
            </FieldBlock>
          </Row>
          <Row>
            <FieldBlock
              label={
                <>
                  Номер плана
                  {!knmPlanFindData?.id && <span className={styles.RedMark}> *</span>}
                </>
              }
              placementLabel="left"
            >
              <div className="shared-row-field">
                <ErknmPlanIdField watchOnly={false} disabled={false} planGuid={undefined} isSkipValidations={true} />
              </div>
            </FieldBlock>
          </Row>
          <Row>
            <FieldBlock
              label={
                <>
                  КНО
                  {!ervkKnoOrganization && <span className={styles.RedMark}> *</span>}
                </>
              }
              placementLabel="left"
            >
              <ErvkKnoOrganisationSearch formRef={formRef} />
            </FieldBlock>
          </Row>

          <Row>
            <PlanKnoChangeHistory />
          </Row>
        </div>
      </form>
    );
  }

  return (
    <>
      <Form<IChangeKnoInPlanForm>
        onSubmit={handleFormSubmit}
        render={renderForm}
        mutators={{ ...arrayMutators }}
        validateOnBlur
      />
    </>
  );
};

const mapStateToProps = (state: IRootState) => ({
  knmPlanFindData: getKnmPlanFindData(state),
});

export const ChangeKnoInPlanForm = connect(mapStateToProps)(ChangeKnoInPlanFormWithoutStore);
